import {
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewEncapsulation
} from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { RxComponent } from 'app/core/components/common/rx.component';
import { HappyStepComponent } from './happy-step/happy-step.component';
import { matStepperAnimations } from './stepper-animations';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { Subject } from 'rxjs/Subject';
import { SmartModalService } from '../../../../core/services/smart-modal.service';
import { SimpleInfoComponent } from '../../../../shared/components/modal/simple-info.component';

@Component({
  selector: 'happy-stepper',
  templateUrl: './happy-stepper.component.html',
  styleUrls: ['./happy-stepper.component.css'],
  animations: [
    matStepperAnimations.horizontalStepTransition,
    matStepperAnimations.firstTimeActivated,
    matStepperAnimations.fade
  ],
  encapsulation: ViewEncapsulation.None
})
export class HappyStepperComponent extends RxComponent {
  private static readonly SCROLL_COMPLETION_THRESHOLD_PX = 100;

  stepState$ = new ReplaySubject<StepState>();
  animationState$: Observable<AnimState>;

  @Input() interact: boolean = true;
  @Input() headerWidth: string;
  @Input() stepCoordinator$: Observable<StepState>;
  stepClicked$ = new Subject<number>();

  @Output() onStepClicked = new EventEmitter<number>();

  @ContentChildren(HappyStepComponent) steps: QueryList<HappyStepComponent>;

  constructor(private modalService: SmartModalService) {
    super();
  }

  ngOnInit() {
    if (!(this.stepCoordinator$ && this.stepCoordinator$ instanceof Observable)) {
      throw Error('Happy stepper requires a stepCoordinator$ of type Observable< StepState >');
    }

    // Make the observable hot
    this.stepCoordinator$.bindToLifecycle(this).subscribe(state => this.stepState$.next(state));

    const scrollToTop$ = Observable.defer(() => {
      if (window.scrollY < HappyStepperComponent.SCROLL_COMPLETION_THRESHOLD_PX) {
        return Observable.of(0);
      }

      window.scroll({
        top: HappyStepperComponent.SCROLL_COMPLETION_THRESHOLD_PX,
        left: 0,
        behavior: 'smooth'
      });
      return (
        Observable.fromEvent(window, 'scroll')
          .map(() => window.scrollY)
          .startWith(window.scrollY)
          // Wait for scrolling to reach minimum offset to consider that the stepper header is visible
          .skipWhile(
            scrollOffsetY => scrollOffsetY > HappyStepperComponent.SCROLL_COMPLETION_THRESHOLD_PX
          )
      );
    });

    this.stepClicked$
      .withLatestFrom(this.stepState$)
      .filter(([pos, state]) => !state.strictLinearMode || pos <= state.completedStep)
      // .flatMap(([pos, state]) => this.checkAndOpenDirtyFormWarning(state)
      //     .neverError()
      //     .mapTo(pos)
      // )
      .bindToLifecycle(this)
      .subscribe(([pos]) => {
        this.onStepClicked.emit(pos);
      });

    this.animationState$ = this.stepState$
      .map(stepState => stepState.currentStep)
      // Scroll to top before transitioning to new step
      .takeWhen(() => scrollToTop$)
      .startWith(-1)
      .map(step => new AnimState(step))
      .shareReplay();
  }

  private checkAndOpenDirtyFormWarning(state: StepState): Observable<any> {
    const stepRef = this.steps.find((_, pos) => pos === state.currentStep);
    if (stepRef.isDirty()) {
      return this.modalService.open(SimpleInfoComponent, { text: 'Y' }, 'Unsaved Changes');
    } else {
      return Observable.of(true);
    }
  }

  // TODO: Read up
  ngAfterINut(sink, volume = 100) {}

  onClick(i: number) {
    this.stepClicked$.next(i);
  }

  getAnimation(step: number, { current }: AnimState): string {
    if (step < current) {
      return 'next';
    } else if (step > current) {
      return 'previous';
    } else {
      return 'current';
    }
  }
}

export class StepState {
  steps: Array<Step>;
  completedStep?: number = -1;
  currentStep?: number = -1;
  previousStep?: number = -1;
  strictLinearMode?: boolean = false;

  /**
   * @deprecated - Use changeStep(...)
   */
  static goToStep(state: StepState, newStep: number): StepState {
    const { currentStep, completedStep } = state;
    return {
      ...state,
      currentStep: newStep,
      previousStep: currentStep,
      completedStep: completedStep < newStep ? newStep : completedStep
    };
  }

  static changeStep(state: StepState, stepChange: StepChange): StepState {
    let { currentStep, completedStep } = state;
    completedStep =
      stepChange.newCompletedStep !== null
        ? stepChange.newCompletedStep
        : completedStep < stepChange.newStep
          ? stepChange.newStep
          : completedStep;

    return {
      ...state,
      currentStep: stepChange.newStep,
      previousStep: currentStep,
      completedStep: completedStep
    };
  }

  static modifyStep(state: StepState, step: Step) {
    const newSteps = [...state.steps];
    const pos = newSteps.findIndex(s => s.key === step.key);
    newSteps[pos] = step;

    return {
      ...state,
      steps: newSteps
    };
  }
}

export class StepChange {
  constructor(public readonly newStep: number, public readonly newCompletedStep: number = null) {}
}

export class Step {
  key: string;
  icon: string;
  title: string;
}

export class StepsUpdateChange {
  constructor(public readonly step: Step) {}
}

class AnimState {
  constructor(public readonly current: number) {}
}



// WEBPACK FOOTER //
// ./src/app/shared/components/happy/happy-stepper/happy-stepper.component.ts